import { ReseterService } from './services/reseter/reseter.service';
import { TranslationsService } from './services/common/translations.service';
import { Platform, NavController, ModalController, MenuController } from '@ionic/angular';
import { ThemeService } from './services/themes/theme.service';
import { Router, NavigationEnd } from '@angular/router';
import { CommonService } from './services/common/common.service';
import { Component } from '@angular/core';
import { ProfileService, UserProfile } from './services/profile/profile.service';

import { AnalyticsService } from './services/common/analytics.service';
import { UserSettingComponent } from './components/user-setting/user-setting.component';
import { ColBreakpoint } from '@class/commons/enums';
import { ThemeInterface } from 'theme/theme-interface';
import { AppAssets, DEFAULT_THEME_NAME, SvgIconNames } from 'theme/theme-map';
import { GenericAlertsToastsService } from '@service/common/generic-alerts-toasts.service';
import { AlertButtonRoles } from '@class/alerts-toasts-props/alert-props';
import { PermissionKey } from '@class/commons/permissions/permission-constants';
import { register } from 'swiper/element/bundle';
import { environment } from 'environments/environment';
import { Loader } from '@googlemaps/js-api-loader';

register();

type MenuItem = {
  name: string;
  routerLink?: string;
  icon?: string;
  keyForPermission?: Array<PermissionKey>;
  isListed: boolean;
};

const googleMapsLoader = new Loader({
  apiKey: environment.services.google.apiKey,
  version: 'quarterly',
});

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
})
export class AppComponent {
  readonly PermissionKey = PermissionKey;
  readonly privacyPolicy = 'https://switchdin.com/privacy-policy';
  readonly termsConditions = 'https://switchdin.com/terms-and-conditions';

  menuList: MenuItem[] = [
    {
      name: 'Admin.Home',
      routerLink: '/home',
      icon: `${AppAssets.ICONS_SVG_MENU}${SvgIconNames.HOME}`,
      isListed: true,
    },
    {
      name: 'General.PortfolioMonitoring',
      routerLink: '/portfolios',
      icon: `${AppAssets.ICONS_SVG_MENU}${SvgIconNames.LOCATION_CITY}`,
      keyForPermission: [PermissionKey.GLOBAL_PORTFOLIO, PermissionKey.GLOBAL_UNIT],
      isListed: true,
    },
    {
      name: 'VirtualPowerPlant.VirtualPowerPlants',
      routerLink: '/virtual_power_plants',
      icon: `${AppAssets.ICONS_SVG_MENU}${SvgIconNames.GRAPHIC_EQ}`,
      keyForPermission: [PermissionKey.GLOBAL_VPP],
      isListed: !this.platform.is('cordova'),
    },
    {
      name: 'Admin.Installer',
      icon: `${AppAssets.ICONS_SVG_MENU}${SvgIconNames.CAST}`,
      keyForPermission: [PermissionKey.GLOBAL_INSTALLER],
      isListed: true,
    },
    {
      name: 'Menu.Support',
      isListed: false, //support item is listed seperately
    },
    {
      name: 'Menu.SignOut',
      routerLink: '/account/login',
      icon: `${AppAssets.ICONS_SVG_MENU}${SvgIconNames.LOGOUT}`,
      isListed: false, //sign-out item is listed separately
    },
  ];

  readonly version = environment.version;
  headerLogoPath = '';
  headerLogoAltName = '';

  userFriendlyName = '';
  isLoggedIn = false;

  logoutIcon = `${AppAssets.ICONS_SVG_MENU}${SvgIconNames.LOGOUT}`;
  permissionIcon = `${AppAssets.ICONS_SVG_MENU}${SvgIconNames.PERMISSIONS}`;
  devicesIcon = `${AppAssets.ICONS_SVG_MENU}${SvgIconNames.DEVICES}`;

  constructor(
    private platform: Platform,
    private theme: ThemeService,
    private router: Router,
    private navController: NavController,
    private trans: TranslationsService,
    private analyticsService: AnalyticsService,
    private reseterService: ReseterService,
    private modalController: ModalController,
    private menu: MenuController,
    private profileService: ProfileService,
    private genericAlertsToastsService: GenericAlertsToastsService,
    private common: CommonService,
  ) {
    this.initializeApp();
  }

  getInstallerRouterLink(): string {
    return this.analyticsService.plugins.posthog.isFeatureEnabled('new-installer-flow')
      ? '/configure-new'
      : '/configure';
  }

  private initializeApp(): void {
    googleMapsLoader.importLibrary('places');
    this.common.loggedInSubject.subscribe((loggedIn) => {
      this.isLoggedIn = loggedIn;
      this.menu.enable(loggedIn);
    });

    this.setRouterEvents();
    this.setLogosIcons();
    this.setUserFriendlyName();

    /** WORKAROUND for detecting windows touch screen devices and setting the platform to desktop
     * - this fixes an issue where Ionic detects a windows touch screen device as mobile and mobileweb devices,
     * - which causes the app to display the mobile version of the app which disallow the user to select text in the app
     * - note Ionic's recommendation to use IonicModule to customise platorm detection doesn't work in Stormcloud app:
     * - https://ionicframework.com/docs/angular/platform#customizing-platform-detection-functions
     */
    if (!this.platform.is('cordova')) {
      const classList = document.querySelector('html').classList;
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        window.navigator.userAgent,
      );
      if (!isMobile) {
        classList.add('plt-desktop');
        classList.remove('plt-mobile', 'plt-mobileweb');
      }
    }

    this.platform
      .ready()
      .then(() => {
        this.setPlatformResize();
        this.setHeightWidthOfPlatform();
        this.setPlatformBackButton();

        this.theme.setTheme();
      })
      .catch((error) => {
        if (window.location.href.indexOf('local') >= 0) {
          console.error(error);
        }
      });
  }

  private setPlatformResize(): void {
    this.platform.resize.subscribe(() => {
      this.setHeightWidthOfPlatform();
      this.common.PLATFORM_RESIZED_OCCURRED_SUBJECT.next(true);
    });
  }
  private setRouterEvents(): void {
    this.router.events.subscribe((ev) => {
      if (ev instanceof NavigationEnd) {
        this.analyticsService.logScreenView(ev.urlAfterRedirects);
        this.menu.close();
      }
    });
  }
  private setLogosIcons(): void {
    this.theme.userTheme$.subscribe((theme: ThemeInterface | null) => {
      if (theme) {
        this.headerLogoPath = theme.headerLogo.path;
        this.headerLogoAltName = theme.name;

        // if user changed the theme
        // the icon for menu 0 should update if the theme is non-switchdin
        this.menuList[0].icon =
          theme.name === DEFAULT_THEME_NAME
            ? `${AppAssets.ICONS_SVG_MENU}${SvgIconNames.HOME}`
            : `${AppAssets.ICONS_SVG_MENU}${SvgIconNames.HOME_WITHOUT_S}`;
      }
    });
  }
  private setUserFriendlyName(): void {
    this.profileService.profile$.subscribe((profile: UserProfile) => {
      this.userFriendlyName = `${profile.first_name} ${profile.last_name}`;
    });
  }

  private setPlatformBackButton(): void {
    this.platform.backButton.subscribe(async () => {
      if (this.router.url === '/home') {
        const { CloseApp } = this.trans.instant('General');
        const alert = await this.genericAlertsToastsService.createAlertWithCloseCancelButton(CloseApp, '', '');

        await alert.present();

        const { role } = await alert.onDidDismiss();
        if (role === AlertButtonRoles.CLOSE) {
          navigator['app'].exitApp();
        }
      } else {
        this.navController.pop();
      }
    });
  }
  private setHeightWidthOfPlatform(): void {
    this.common.PLATFORM_HEIGHT = this.platform.height();
    this.common.PLATFORM_WIDTH = this.platform.width();
    if (this.common.PLATFORM_WIDTH < ColBreakpoint.LG) {
      this.common.OPEN_DEVICE_CONTROLLER_IN_MODAL = true;
    } else {
      this.common.OPEN_DEVICE_CONTROLLER_IN_MODAL = false;
    }
  }

  async logout() {
    try {
      await this.reseterService.run();

      const { Success } = this.trans.instant('Logout');
      const toast = await this.genericAlertsToastsService.createCustomToast(Success);
      await toast.present();
    } catch (error) {
      console.error(error);
    }
  }

  public async presentProfileModal(): Promise<void> {
    this.menu?.close();
    const modal = await this.modalController.create({
      component: UserSettingComponent,
      cssClass: 'large-modal',
      backdropDismiss: false,
    });
    await modal.present();
  }
}
